/**
 * React Query Hooks for API Data Fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as tasksApi from '../services/tasks.api';
import * as routesApi from '../services/routes.api';
import * as zonesApi from '../services/zones.api';
import * as driversApi from '../services/drivers.api';
import * as schedulesApi from '../services/schedules.api';
import { getDriverRoutes } from '../services/driverMonitor.api';
import { getBaghdadOperationalDate } from '../utils/date';

// ============================================================================
// Tasks Hooks
// ============================================================================

export function useTasks(
  dates?: string | string[] | tasksApi.TaskDateRange,
  statusSlugs?: string[]
) {
  return useQuery({
    queryKey: ['tasks', dates, statusSlugs],
    queryFn: () => tasksApi.getTasks(dates, statusSlugs),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useTaskCountsByDateRange(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['tasks', 'counts-by-date', startDate, endDate],
    queryFn: () => tasksApi.getTaskCountsByDateRange(startDate, endDate),
    enabled: !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useTaskSummary(cityId: number, date?: string) {
  return useQuery({
    queryKey: ['tasks', 'summary', cityId, date],
    queryFn: () => tasksApi.getTaskSummary(cityId, date),
    enabled: !!cityId,
  });
}

// ============================================================================
// Routes Hooks
// ============================================================================

export function useOptimizeRoutes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: routesApi.optimizeRoutes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeRuns'] });
    },
  });
}

export function useRouteRuns(cityId: number, date?: string) {
  return useQuery({
    queryKey: ['routeRuns', cityId, date],
    queryFn: () => routesApi.getRouteRuns(cityId, date),
    enabled: !!cityId,
  });
}

export function useRoutesByDate(cityId: number, date?: string) {
  return useQuery({
    queryKey: ['routes', cityId, date],
    queryFn: () => routesApi.getRoutesByDate(cityId, date),
    enabled: !!cityId,
    refetchOnMount: 'always',
  });
}

export function useRouteRun(runId: string) {
  return useQuery({
    queryKey: ['routeRun', runId],
    queryFn: () => routesApi.getRouteRun(runId),
    enabled: !!runId,
  });
}

export function useRoute(routeId: string) {
  return useQuery({
    queryKey: ['route', routeId],
    queryFn: () => routesApi.getRoute(routeId),
    enabled: !!routeId,
  });
}

export function usePublishRoutes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: routesApi.publishRoutes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routeRuns'] });
      queryClient.invalidateQueries({ queryKey: ['routeRun'] });
    },
  });
}

export function useReassignTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ taskUid, toRouteId, insertAtSequence }: {
      taskUid: string;
      toRouteId: string;
      insertAtSequence?: number;
    }) => routesApi.reassignTask(taskUid, toRouteId, insertAtSequence),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['route'] });
      queryClient.invalidateQueries({ queryKey: ['routeRun'] });
    },
  });
}

export function useReorderStops() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ routeId, newOrder }: { routeId: string; newOrder: string[] }) =>
      routesApi.reorderStops(routeId, newOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['route'] });
    },
  });
}

export function useCreateRoutes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: routesApi.createRoutes,
    onSuccess: () => {
      // Invalidate routes queries
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      queryClient.invalidateQueries({ queryKey: ['routeRuns'] });
      // Invalidate tasks to update planned status
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDeleteRoute() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (routeId: string) => routesApi.deleteRoute(routeId),
    onSuccess: () => {
      // Invalidate routes queries
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      queryClient.invalidateQueries({ queryKey: ['routeRuns'] });
      queryClient.invalidateQueries({ queryKey: ['route'] });
      // Invalidate tasks to update planned status (task is now unplanned)
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// ============================================================================
// Zones Hooks
// ============================================================================

export function useZones(cityId: number, options?: { includeStats?: boolean }) {
  return useQuery({
    queryKey: ['zones', cityId, options],
    queryFn: () => zonesApi.getZones(cityId, options),
    enabled: !!cityId,
  });
}

export function useZone(zoneId: string) {
  return useQuery({
    queryKey: ['zone', zoneId],
    queryFn: () => zonesApi.getZone(zoneId),
    enabled: !!zoneId,
  });
}

export function useCreateZone() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: zonesApi.createZone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
    },
  });
}

export function useUpdateZone() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ zoneId, params }: { zoneId: string; params: zonesApi.UpdateZoneParams }) =>
      zonesApi.updateZone(zoneId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      queryClient.invalidateQueries({ queryKey: ['zone'] });
    },
  });
}

export function useDeleteZone() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ zoneId, hard }: { zoneId: string; hard?: boolean }) =>
      zonesApi.deleteZone(zoneId, hard),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
    },
  });
}

export function useZoneTaskDistribution(cityId: number, date?: string) {
  return useQuery({
    queryKey: ['zoneDistribution', cityId, date],
    queryFn: () => zonesApi.getZoneTaskDistribution(cityId, date),
    enabled: !!cityId,
  });
}

// ============================================================================
// Drivers Hooks
// ============================================================================

export function useDrivers() {
  return useQuery({
    queryKey: ['drivers'],
    queryFn: () => driversApi.getDrivers(),
  });
}

export function useAvailableDrivers(cityId: number, date?: string) {
  return useQuery({
    queryKey: ['drivers', 'available', cityId, date],
    queryFn: () => driversApi.getAvailableDrivers(cityId, date),
    enabled: !!cityId,
  });
}

export function useDriver(driverUid: string) {
  return useQuery({
    queryKey: ['driver', driverUid],
    queryFn: () => driversApi.getDriver(driverUid),
    enabled: !!driverUid,
  });
}

export function useDriverAvailability(
  driverUid: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['driverAvailability', driverUid, startDate, endDate],
    queryFn: () => driversApi.getDriverAvailability(driverUid, startDate, endDate),
    enabled: !!driverUid,
  });
}

// 60 min matches the legend's "Stale (< 60 min)": with 30, working drivers left the list between sparse GPS points (prod 2026-11-03: 883 vs 317 min off-list over 15 drivers).
export function useLiveDriverPositions(cityId: number, maxAgeMinutes = 60) {
  return useQuery({
    queryKey: ['driverPositions', cityId, maxAgeMinutes],
    queryFn: () => driversApi.getLiveDriverPositions(cityId, maxAgeMinutes),
    enabled: !!cityId,
  });
}

// Selected driver on the Live Monitor: today's GPS path and today's route stops.
// No refetchInterval: the page's Auto-refresh refetches them, so unticking it stops them too.
export function useDriverTrack(driverUid?: string) {
  const opDate = getBaghdadOperationalDate();
  return useQuery({
    queryKey: ['driverTrack', driverUid, opDate],
    queryFn: () => driversApi.getDriverTrack(driverUid!, new Date(`${opDate}T04:00:00+03:00`)),
    enabled: !!driverUid,
  });
}

export function useDriverRoutesToday(driverUid?: string) {
  const opDate = getBaghdadOperationalDate();
  return useQuery({
    queryKey: ['driverRoutesToday', driverUid, opDate],
    queryFn: () => getDriverRoutes(new Date(`${opDate}T12:00:00+03:00`), [driverUid!]),
    enabled: !!driverUid,
  });
}

// ============================================================================
// Schedules Hooks
// ============================================================================

export function useSchedules() {
  return useQuery({
    queryKey: ['schedules'],
    queryFn: schedulesApi.getAllSchedules,
  });
}

export function useSchedule(driverUid: string) {
  return useQuery({
    queryKey: ['schedule', driverUid],
    queryFn: () => schedulesApi.getSchedule(driverUid),
    enabled: !!driverUid,
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: schedulesApi.createSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ driverUid, params }: {
      driverUid: string;
      params: schedulesApi.UpdateScheduleParams;
    }) => schedulesApi.updateSchedule(driverUid, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
}

export function useAddException() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ driverUid, exception }: {
      driverUid: string;
      exception: schedulesApi.AddExceptionParams;
    }) => schedulesApi.addException(driverUid, exception),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      queryClient.invalidateQueries({ queryKey: ['driverAvailability'] });
    },
  });
}

export function useMarkUnavailable() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ driverUid, startDate, endDate, reason }: {
      driverUid: string;
      startDate: string;
      endDate: string;
      reason?: string;
    }) => schedulesApi.markUnavailable(driverUid, startDate, endDate, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      queryClient.invalidateQueries({ queryKey: ['driverAvailability'] });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (driverUid: string) => schedulesApi.deleteSchedule(driverUid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
}
